import { useState, useEffect, createContext, useContext } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { logSessionTimeout } from "@/lib/auditLog";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);
const isDev = import.meta.env.DEV;

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let timeoutWarning: NodeJS.Timeout;
    
    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
        
        // Set up session timeout warning when user signs in
        if (event === 'SIGNED_IN' && session?.user) {
          if (timeoutWarning) clearTimeout(timeoutWarning);
          timeoutWarning = setTimeout(() => {
            logSessionTimeout(session.user.id);
            if (isDev) console.warn('[AUTH] Session will expire soon. Please refresh your session.');
          }, 55 * 60 * 1000);
        } else if (event === 'SIGNED_OUT') {
          if (timeoutWarning) clearTimeout(timeoutWarning);
          if (isDev) console.log('[AUTH] User signed out');
        } else if (event === 'TOKEN_REFRESHED') {
          if (isDev) console.log('[AUTH] Session refreshed successfully');
        }
      }
    );

    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => {
      subscription.unsubscribe();
      if (timeoutWarning) clearTimeout(timeoutWarning);
    };
  }, []); // Empty dependency array - only run once on mount

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};